import { useCallback, useRef, useState } from "react";

export interface ActiveSession {
  /** The open session, or undefined for a fresh chat. */
  activeSession: string | undefined;
  /** Key for the chat provider: the open session, or the current fresh-chat generation. */
  providerKey: string;
  select: (sessionId: string) => void;
  startNewChat: () => void;
  /**
   * Drop a session that no longer exists on the backend. Resets to a fresh chat only if that
   * session is still open *now* — a delete resolves asynchronously, so comparing against a
   * captured render value could wipe a newer selection.
   */
  discard: (sessionId: string) => void;
}

export function useActiveSession(): ActiveSession {
  const [activeSession, setActiveSession] = useState<string>();
  const [newChatNonce, setNewChatNonce] = useState(0);
  const activeSessionRef = useRef<string | undefined>(undefined);

  // Single writer keeps the ref in step with the state, so `discard` sees the selection as of
  // after an await rather than when its callback was created.
  const open = useCallback((sessionId: string | undefined) => {
    activeSessionRef.current = sessionId;
    setActiveSession(sessionId);
  }, []);

  const select = useCallback((sessionId: string) => open(sessionId), [open]);

  const startNewChat = useCallback(() => {
    setNewChatNonce((n) => n + 1);
    open(undefined);
  }, [open]);

  const discard = useCallback(
    (sessionId: string) => {
      if (activeSessionRef.current === sessionId) startNewChat();
    },
    [startNewChat],
  );

  return {
    activeSession,
    providerKey: activeSession ?? `new-${newChatNonce}`,
    select,
    startNewChat,
    discard,
  };
}
